import { createSlice } from '@reduxjs/toolkit';
import { AppThunk } from './store';
import { apiString as API_URL } from '../utils/constants';
import axios from 'axios';
import { setIsLoading } from './shared.slice';
import ICPTModifier, { CPTModifier } from '../Models/Shared/CPTModifier.model';

const adminCPTModifiersSlice = createSlice({
    name: 'adminCPTModifiersSlice',
    initialState: {
        modifiers: [] as ICPTModifier[],
        selectedModifier: new CPTModifier() as ICPTModifier,
        showFormModal: false,
        showDeleteModal: false,
    },
    reducers: {
        _setModifiers(state, action) {
            state.modifiers = action.payload;
        },
        _setSelectedModifier(state, action) {
            state.selectedModifier = action.payload;
        },
        _setShowFormModal(state, action) {
            state.showFormModal = action.payload;
        },
        _setShowDeleteModal(state, action) {
            state.showDeleteModal = action.payload;
        },
    },
});

const { _setModifiers, _setSelectedModifier, _setShowFormModal, _setShowDeleteModal } = adminCPTModifiersSlice.actions;

export const setModifiers =
    (modifiers: ICPTModifier[]): AppThunk =>
    (dispatch) => {
        dispatch(_setModifiers(modifiers));
    };

export const setSelectedModifier =
    (modifier: ICPTModifier): AppThunk =>
    (dispatch) => {
        dispatch(_setSelectedModifier(modifier));
    };

export const setShowFormModal =
    (show: boolean): AppThunk =>
    (dispatch) => {
        dispatch(_setShowFormModal(show));
    };

export const setShowDeleteModal =
    (show: boolean): AppThunk =>
    (dispatch) => {
        dispatch(_setShowDeleteModal(show));
    };

export const fetchAllModifiers = (): AppThunk => async (dispatch) => {
    dispatch(setIsLoading(true));
    try {
        const response = await axios.get(`${API_URL}/encounter/getallmodifiers`);
        dispatch(_setModifiers(response.data as ICPTModifier[]));
    } catch (error) {
        console.error('Error fetching modifiers:', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const fetchActiveModifiers = (): AppThunk => async (dispatch) => {
    dispatch(setIsLoading(true));
    try {
        const response = await axios.get(`${API_URL}/encounter/getactivemodifiers`);
        dispatch(_setModifiers(response.data as ICPTModifier[]));
    } catch (error) {
        console.error('Error fetching modifiers:', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const createModifier =
    (modifier: ICPTModifier): AppThunk =>
    async (dispatch) => {
        dispatch(setIsLoading(true));
        try {
            await axios.post(`${API_URL}/encounter/createmodifier`, modifier);
            dispatch(fetchAllModifiers());
            dispatch(_setShowFormModal(false));
        } catch (error) {
            console.error('Error creating modifier:', error);
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const updateModifier =
    (modifier: ICPTModifier): AppThunk =>
    async (dispatch) => {
        try {
            dispatch(setIsLoading(true));
            await axios.put(`${API_URL}/encounter/updatemodifier`, modifier);
            dispatch(fetchAllModifiers());
            dispatch(_setShowFormModal(false));
        } catch (error) {
            console.error('Error updating modifier:', error);
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const deleteModifier =
    (id: string): AppThunk =>
    async (dispatch) => {
        try {
            dispatch(setIsLoading(true));
            await axios.delete(`${API_URL}/encounter/deletecptmodifier/${id}`);
            dispatch(fetchAllModifiers());
            dispatch(_setShowDeleteModal(false));
        } catch (error) {
            console.error('Error deleting modifier:', error);
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export default adminCPTModifiersSlice.reducer;
